import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { NavLink, useNavigate } from "react-router-dom";
import { Bell, ChevronRight, X, ExternalLink } from "lucide-react";
import { cn } from "@ridereserv/shared/utils";
import { api } from "@/lib/api";
import { formatRelativeTime } from "@/lib/relativeTime";
import { getNotificationDeepLink, typeIcons } from "./notificationDisplay";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  readAt: string | null;
  resourceType: string | null;
  resourceId: string | null;
  actionUrl: string | null;
  actionLabel: string | null;
  companyId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  company: {
    id: string;
    name: string;
  } | null;
}

interface NotificationsResponse {
  success: boolean;
  data: {
    notifications: Notification[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  };
}

interface UnreadCountResponse {
  success: boolean;
  data: {
    count: number;
  };
}

interface NotificationBellProps {
  /**
   * Trigger tone. "default" is for light surfaces; "inverse" styles the
   * trigger for the dark ink rail. The dropdown stays a light popover.
   */
  tone?: "default" | "inverse";
}

/**
 * Notification bell with dropdown showing recent notifications.
 * Shows unread count badge and allows marking as read.
 * @param root0 - Component props.
 * @param root0.tone - Trigger tone (default, inverse).
 * @returns React element for the notification bell component
 */
export function NotificationBell({ tone = "default" }: NotificationBellProps = {}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch unread count
  const { data: unreadData } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: async () => {
      const response = await api.get<UnreadCountResponse>("/notifications/unread-count");
      return response.data.data.count;
    },
    refetchInterval: 60000, // Refetch every 30 seconds
  });

  // Fetch notifications when dropdown is open
  const { data: notifications } = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: async () => {
      const response = await api.get<NotificationsResponse>("/notifications?limit=10");
      return response.data.data.notifications;
    },
    enabled: isOpen,
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationIds: string[]) => {
      await api.post("/notifications/mark-read", { notificationIds });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await api.post("/notifications/mark-all-read");
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const unreadCount = unreadData ?? 0;

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsReadMutation.mutate([notification.id]);
    }
    setIsOpen(false);
  };

  /**
   * Open a deep-linked notification (e.g. a ride offer): mark it read, close
   * the dropdown, and navigate to the linked in-app route.
   * @param notification - The clicked notification.
   * @param deepLink - The client-side route to navigate to.
   */
  const handleDeepLinkOpen = (notification: Notification, deepLink: string) => {
    handleNotificationClick(notification);
    void navigate(deepLink);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => { setIsOpen(!isOpen); }}
        className={cn(
          "relative flex min-h-11 min-w-11 items-center justify-center rounded-sm transition-colors pointer-fine:min-h-8 pointer-fine:min-w-8",
          tone === "inverse"
            ? "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground"
            : "text-neutral-600 hover:bg-neutral-100"
        )}
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive-500 px-1 text-[10px] font-medium text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown — anchored left when the trigger sits in the ink rail so the
          popover opens into the page instead of off-screen */}
      {isOpen && (
        <div
          className={cn(
            "absolute top-full z-50 mt-2 w-80 overflow-hidden rounded-lg border border-neutral-200 bg-surface shadow-lg sm:w-96",
            tone === "inverse" ? "left-0" : "right-0"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3">
            <h3 className="font-semibold text-neutral-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => { markAllAsReadMutation.mutate(); }}
                className="flex min-h-11 items-center rounded-md text-xs font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto">
            {notifications && notifications.length > 0 ? (
              notifications.map((notification) => {
                const Icon = typeIcons[notification.type] ?? Bell;
                // Ride offers deep-link straight to that ride request — the
                // whole row becomes the tap target.
                const deepLink = getNotificationDeepLink(notification);

                return (
                  <div
                    key={notification.id}
                    className={cn(
                      "border-b border-neutral-100 p-4 last:border-0",
                      !notification.isRead && "bg-primary-50",
                      deepLink &&
                        "cursor-pointer transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                    )}
                    role={deepLink ? "button" : undefined}
                    tabIndex={deepLink ? 0 : undefined}
                    onClick={
                      deepLink ? () => { handleDeepLinkOpen(notification, deepLink); } : undefined
                    }
                    onKeyDown={
                      deepLink
                        ? (e) => {
                            // Only when the row itself is focused — Enter/Space on
                            // nested controls (mark-read, action link) must keep
                            // their own behavior, not hijack into navigation.
                            if (e.target !== e.currentTarget) return;
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleDeepLinkOpen(notification, deepLink);
                            }
                          }
                        : undefined
                    }
                  >
                    <div className="flex gap-3">
                      <div
                        className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                          notification.type === "driver_approved"
                            ? "bg-success-100 text-success-600"
                            : notification.type === "driver_rejected"
                              ? "bg-destructive-100 text-destructive-600"
                              : "bg-primary-100 text-primary-600"
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-neutral-900">
                          {notification.title}
                        </p>
                        <p className="mt-0.5 text-xs text-neutral-600 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-xs text-neutral-400">
                            {formatRelativeTime(notification.createdAt)}
                          </span>
                          {notification.actionUrl && (
                            <NavLink
                              to={deepLink ?? notification.actionUrl}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotificationClick(notification);
                              }}
                              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                            >
                              {notification.actionLabel ?? "View"}
                              {deepLink ? (
                                <ChevronRight className="h-3 w-3" aria-hidden="true" />
                              ) : (
                                <ExternalLink className="h-3 w-3" />
                              )}
                            </NavLink>
                          )}
                        </div>
                      </div>
                      {!notification.isRead && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsReadMutation.mutate([notification.id]);
                          }}
                          className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md text-neutral-400 transition-colors hover:text-neutral-600"
                          aria-label="Mark as read"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                      {deepLink && (
                        <ChevronRight
                          className="h-3.5 w-3.5 shrink-0 self-center text-neutral-400"
                          strokeWidth={1.75}
                          aria-hidden="true"
                        />
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-8 text-center">
                <Bell className="mx-auto h-8 w-8 text-neutral-300" />
                <p className="mt-2 text-sm text-neutral-500">No notifications yet</p>
              </div>
            )}
          </div>

          {/* View all link */}
          <div className="border-t border-neutral-100 p-2">
            <NavLink
              to="/account/notifications"
              onClick={() => { setIsOpen(false); }}
              className="flex min-h-11 items-center justify-center rounded-md px-3 py-2 text-center text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
            >
              View all notifications
            </NavLink>
          </div>
        </div>
      )}
    </div>
  );
}
